import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, tap } from 'rxjs';

// Helpers & Envs
import { handleError } from '@shared-functions/handle-error.function';
import { environment } from '@environments/environment';

// Interfaces
import { AuthResponse } from '../interfaces/auth-response.interface';
import { Credenciales } from '../interfaces/credenciales.interface';
import { JWTPayload } from '../interfaces/jwt-payload.interface';
import { CurrentSimpleUser } from '../interfaces/current-simple-user.interface';

// Services
import { LocalStorageService } from '@core-services/local-storage.service';
import { TokenService } from './token.service';
import { PermisosService } from '@permisos/services/permisos.service';
import { ChangePasswordForm } from '@auth/interfaces/change-password.interface';
import { Router } from '@angular/router';
import { PortalService } from 'src/app/portal/services/portal.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly API_SERVER = environment.API_SERVER;

  private readonly _currentUser = new BehaviorSubject<CurrentSimpleUser | null>(null);

  public readonly currentUser$ = this._currentUser.asObservable();

  private emitSessionChanged(): void {
    if (typeof globalThis.dispatchEvent === 'function') {
      globalThis.dispatchEvent(new Event('app-session-changed'));
    }
  }

  constructor(
    private readonly http: HttpClient,
    private readonly localStorageService: LocalStorageService,
    private readonly tokenService: TokenService,
    private readonly permisosService: PermisosService,
    private readonly portalService: PortalService,
    private readonly router: Router,
  ) {
    this.checkCurrentSession();
  }

  get currentUserValue(): CurrentSimpleUser | null {
    return this._currentUser.value;
  }

  signIn(credenciales: Credenciales): Observable<AuthResponse> {
    const signInCreds = { ...credenciales, desdeWeb: 1 };

    return this.http.post<AuthResponse>(`${this.API_SERVER}/auth/signin`, signInCreds).pipe(
      tap((response) => this.handleSession(response.token)),
      catchError(handleError),
    );
  }

  hasPermission(requiredPermission: string): boolean {
    const user = this.currentUserValue;

    if (!user) return false;

    // El superadmin pasa siempre, igual que en el backend
    // (jwt.service.js: `if (req.isSuperAdmin) return true`).
    //
    // Sin esto los dos lados no coincidían: el backend le permitía todo y el
    // frontend solo lo que estuviera literalmente en su lista de permisos. El
    // efecto era que cada permiso nuevo del catálogo, mientras no se sembrara
    // y asignara, dejaba al adminroot fuera de una pantalla que el backend sí
    // le habilitaba — y como PermissionGuard redirige a /home, parecía un
    // cierre de sesión.
    //
    // El claim viaja en el token firmado y el backend valida la firma y vuelve
    // a decidir por su cuenta: acá solo gobierna qué se muestra.
    if (user.isSuperAdmin) return true;

    if (!user.permissions) return false;

    if (user.permissions.includes('*')) return true;

    if (user.permissions.includes(requiredPermission)) return true;

    const module = requiredPermission.split(':')[0];
    if (module && user.permissions.includes(`${module}:*`)) return true;

    return false;
  }

  getMissingPermissions(required: string[]): string[] {
    return required.filter((p) => !this.hasPermission(p));
  }

  private handleSession(token: string): void {
    this.tokenService.setToken(token);
    this.emitSessionChanged();

    const payload: JWTPayload = this.tokenService.getPayload(token);
    const parsedUsuarioId = Number(payload.usuarioId);
    const usuarioId = Number.isNaN(parsedUsuarioId) ? undefined : parsedUsuarioId;

    const perms = payload.security?.permissions || payload.security.permissions || [];

    const user: CurrentSimpleUser = {
      sessionUser: payload.username,
      usuarioId,
      rolId: payload.rolId,
      entidadId: payload.entidadId && payload.entidadId !== 'null' ? payload.entidadId : null,
      permissions: perms,
      isSuperAdmin: payload.isSuperAdmin === true,
      scope: payload.security?.scope || 'PROPIO',
      nombreRol: payload.nombreRol,
      nombreEntidad: payload.nombreEntidad,
      slug: payload.slug,
    };

    this._currentUser.next(user);
  }

  private checkCurrentSession(): void {
    const token = this.tokenService.getToken();

    if (token && !this.tokenService.isExpired()) {
      this.handleSession(token);
    } else {
      this.clearSessionState();
    }
  }
  private clearSessionState(): void {
    this.tokenService.clearToken();
    this.emitSessionChanged();
    this.localStorageService.clearLocalStorage();
    this.localStorageService.setItems([]);
    this.permisosService.limpiarPermisos();
    this._currentUser.next(null);
  }
  /*   logOut(): void {
    const slug = this.currentUserValue?.slug;
    this.localStorageService.clearLocalStorage();
    this.localStorageService.setItems([]);
    this.permisosService.limpiarPermisos();
    this._currentUser.next(null);
    console.log('tenemos_slug?', slug);
    if (slug) {
      this.router.navigate([`/${slug}/login`]);
    } else {
      this.router.navigate(['/']);
    }
  } */
  logOut(): void {
    // 1. Guardamos el slug antes de limpiar
    const slug = this.currentUserValue?.slug;

    // 2. Limpiamos la memoria
    this.clearSessionState();

    // 3. Navegamos (porque logOut sí es una acción explícita del usuario o expiración)
    if (slug) {
      this.router.navigate([`/${slug}/login`]);
    } else {
      this.router.navigate(['/']);
    }
  }
  isLoggedIn(): boolean {
    return !!this.currentUserValue && !this.tokenService.isExpired();
  }

  changePassword(
    changePasswordForm: ChangePasswordForm,
    sessionUser: string,
  ): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.API_SERVER}/auth/change-password`, changePasswordForm)
      .pipe(
        tap((authResponse: AuthResponse) => {
          if (authResponse.token) {
            this.handleSession(authResponse.token);
          }
        }),
        catchError(handleError),
      );
  }
}
